import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { caseFileAPI, masrafAPI } from '../../api/api';
import { formatDateToLocalString, getTodayDateString, getTodayISOString } from '../../utils/dateUtils';
import './HesapDetayi.css';
import { onayla } from '../../utils/onayla';

const KDV_ORANI = 0.20; // %20 KDV

// Yasal faiz (kanuni faiz) oranları — 3095 sayılı Kanun md.1
// Her kayıt, o tarihten itibaren geçerli olan yıllık oranı ifade eder.
const YASAL_FAIZ_TABLOSU = [
  { baslangic: '1984-12-19', oran: 30 },
  { baslangic: '1998-01-01', oran: 50 },
  { baslangic: '2000-01-01', oran: 60 },
  { baslangic: '2002-07-01', oran: 55 },
  { baslangic: '2003-07-01', oran: 50 },
  { baslangic: '2004-01-01', oran: 43 },
  { baslangic: '2005-05-01', oran: 12 },
  { baslangic: '2006-01-01', oran: 9 },
  { baslangic: '2024-06-01', oran: 24 },
  { baslangic: '2026-07-31', oran: 31 }
].map((r) => ({ oran: r.oran, baslangic: new Date(r.baslangic + 'T00:00:00Z') }));

const MS_GUN = 24 * 60 * 60 * 1000;

const DEFAULT_VALUES = {
  gelenToplamOdeme: '',
  asilAlacak: '',
  faizBaslangic: '',
  faizBitis: '',
  avukatlikOrani: '',
  kdvDahil: false,
  yargilamaGiderleri: '',
  muvekkildenDusulecekMasraf: '',
  berileVerilecek: '',
  berileManuel: false
};

const KNOWN_KEYS = Object.keys(DEFAULT_VALUES);

const formatCurrency = (amount) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(
    Number.isFinite(amount) ? amount : 0
  );

const formatTarihTR = (str) => {
  if (!str) return '';
  const d = new Date(str + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
};

const parseNum = (value) => {
  if (value === '' || value === null || value === undefined) return 0;
  const n = parseFloat(String(value).replace(',', '.'));
  return Number.isNaN(n) ? 0 : n;
};

const toUTCDate = (str) => {
  if (!str) return null;
  const d = new Date(str + 'T00:00:00Z');
  return Number.isNaN(d.getTime()) ? null : d;
};

const yeniId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const yeniHesap = (baslik) => ({
  id: yeniId(),
  baslik: baslik || 'Ödeme',
  masrafId: null,
  yargilamaDahilIds: undefined,
  ...DEFAULT_VALUES
});

const masrafIdOf = (m) => {
  const id = m?.Id ?? m?.id;
  if (id == null || id === '') return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : null;
};

const masrafTuruOf = (m) =>
  String(m?.MasrafTuru || m?.masrafTuru || m?.CustomMasrafTuru || m?.customMasrafTuru || '').trim();

const masrafTutarOf = (m) => {
  const n = parseFloat(m?.Tutar ?? m?.tutar);
  return Number.isFinite(n) ? n : 0;
};

const normalizeTur = (str) =>
  String(str || '')
    .trim()
    .toLocaleUpperCase('tr-TR')
    .replace(/İ/g, 'I')
    .replace(/Ü/g, 'U')
    .replace(/Ö/g, 'O')
    .replace(/Ş/g, 'S')
    .replace(/Ç/g, 'C')
    .replace(/Ğ/g, 'G');

const isGelenOdemeMasraf = (m) => {
  const t = normalizeTur(masrafTuruOf(m));
  return t.startsWith('GELEN ODEME') || t.startsWith('GELEN ÖDEME');
};

const isMuvekkileOdemeMasraf = (m) => {
  const t = normalizeTur(masrafTuruOf(m));
  return t.startsWith('MUVEKKILE YAPILAN ODEME') || t.startsWith('MÜVEKKİLE YAPILAN ÖDEME');
};

const isBerileMasraf = (m) => {
  if (m?.IsBerileVerilen || m?.isBerileVerilen) return true;
  const t = normalizeTur(masrafTuruOf(m));
  return t === 'BERILE VERILEN' || t === 'BERİLE VERİLEN';
};

const isYargilamaGiderMasraf = (m) => {
  if (!masrafTuruOf(m)) return false;
  if (m?.IsGelir || m?.isGelir) return false;
  if (isGelenOdemeMasraf(m) || isMuvekkileOdemeMasraf(m) || isBerileMasraf(m)) return false;
  return true;
};

const masrafTarihKey = (m) =>
  formatDateToLocalString(m?.CreatedDate ?? m?.createdDate) ||
  formatDateToLocalString(m?.SonTarih ?? m?.sonTarih) ||
  formatDateToLocalString(m?.Tarih ?? m?.tarih) ||
  formatDateToLocalString(m?.MasrafIstenmeTarihi ?? m?.masrafIstenmeTarihi) ||
  '';

/** Yalnızca bugün ve sonrası girilen gelen ödemeler hesap detayına aktarılır. */
const isMasrafBugunVeyaSonra = (m) => {
  const key = masrafTarihKey(m);
  if (!key) return false;
  return key >= getTodayDateString();
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const yargilamaToplamOf = (calc, giderler) => {
  const ids = new Set((calc.yargilamaDahilIds || []).map(Number));
  return (giderler || []).reduce((sum, m) => {
    const id = masrafIdOf(m);
    if (id == null || !ids.has(id)) return sum;
    return sum + masrafTutarOf(m);
  }, 0);
};

const sozlesmeOraniStr = (caseFile) => {
  const v = caseFile?.SozlesmeOrani ?? caseFile?.sozlesmeOrani;
  if (v == null || v === '') return '';
  const n = parseFloat(v);
  return Number.isFinite(n) ? String(n) : '';
};

const sozlesmeKdvDahil = (caseFile) =>
  !!(caseFile?.SozlesmeOraniKdvDahil ?? caseFile?.sozlesmeOraniKdvDahil);

const hesaplaYasalFaiz = (anapara, baslangicStr, bitisStr) => {
  const bas = toUTCDate(baslangicStr);
  const bit = toUTCDate(bitisStr);
  if (!anapara || anapara <= 0 || !bas || !bit || bit <= bas) {
    return { faiz: 0, segmentler: [], toplamGun: 0 };
  }

  let faiz = 0;
  let toplamGun = 0;
  const segmentler = [];

  for (let i = 0; i < YASAL_FAIZ_TABLOSU.length; i++) {
    const donemBas = YASAL_FAIZ_TABLOSU[i].baslangic;
    const donemBit =
      i < YASAL_FAIZ_TABLOSU.length - 1
        ? YASAL_FAIZ_TABLOSU[i + 1].baslangic
        : new Date('2999-01-01T00:00:00Z');

    const kesisimBas = new Date(Math.max(bas.getTime(), donemBas.getTime()));
    const kesisimBit = new Date(Math.min(bit.getTime(), donemBit.getTime()));
    const gun = Math.round((kesisimBit.getTime() - kesisimBas.getTime()) / MS_GUN);

    if (gun > 0) {
      const oran = YASAL_FAIZ_TABLOSU[i].oran;
      const segFaiz = anapara * (oran / 100) * (gun / 365);
      faiz += segFaiz;
      toplamGun += gun;
      segmentler.push({ oran, gun, faiz: segFaiz });
    }
  }

  return { faiz, segmentler, toplamGun };
};

const AYLAR = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık'
];

const TarihSecici = ({ value, onChange }) => {
  const bol = (v) => (v ? v.split('-') : ['', '', '']);
  const ilk = bol(value);
  const [yil, setYil] = useState(ilk[0] || '');
  const [ay, setAy] = useState(ilk[1] ? String(parseInt(ilk[1], 10)) : '');
  const [gun, setGun] = useState(ilk[2] ? String(parseInt(ilk[2], 10)) : '');

  useEffect(() => {
    const p = bol(value);
    setYil(p[0] || '');
    setAy(p[1] ? String(parseInt(p[1], 10)) : '');
    setGun(p[2] ? String(parseInt(p[2], 10)) : '');
  }, [value]);

  const emit = (g, a, y) => {
    if (g && a && y) {
      onChange(`${y}-${String(a).padStart(2, '0')}-${String(g).padStart(2, '0')}`);
    } else {
      onChange('');
    }
  };

  const suAnkiYil = new Date().getFullYear();
  const yillar = [];
  for (let y = suAnkiYil + 1; y >= 1990; y--) yillar.push(y);

  return (
    <div className="hd-date-selects">
      <select
        className="hd-date-select"
        value={gun}
        onChange={(e) => {
          setGun(e.target.value);
          emit(e.target.value, ay, yil);
        }}
      >
        <option value="">Gün</option>
        {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
          <option key={d} value={d}>
            {d}
          </option>
        ))}
      </select>
      <select
        className="hd-date-select"
        value={ay}
        onChange={(e) => {
          setAy(e.target.value);
          emit(gun, e.target.value, yil);
        }}
      >
        <option value="">Ay</option>
        {AYLAR.map((m, i) => (
          <option key={i + 1} value={i + 1}>
            {m}
          </option>
        ))}
      </select>
      <select
        className="hd-date-select"
        value={yil}
        onChange={(e) => {
          setYil(e.target.value);
          emit(gun, ay, e.target.value);
        }}
      >
        <option value="">Yıl</option>
        {yillar.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );
};

const hesapla = (calc, yargilamaToplam = 0) => {
  const asilAlacak = parseNum(calc.asilAlacak);
  const oran = parseNum(calc.avukatlikOrani);
  const muvekkildenDusulecek = parseNum(calc.muvekkildenDusulecekMasraf);
  const gelenToplamOdeme = parseNum(calc.gelenToplamOdeme);

  const faizDetay = hesaplaYasalFaiz(asilAlacak, calc.faizBaslangic, calc.faizBitis);
  const faiz = faizDetay.faiz;

  const araToplam = asilAlacak + faiz;
  const avukatlikUcretiNet = araToplam * (oran / 100);
  const kdvTutari = calc.kdvDahil ? avukatlikUcretiNet * KDV_ORANI : 0;
  const avukatlikUcreti = avukatlikUcretiNet + kdvTutari;

  const muvekkileOdenecek = araToplam - avukatlikUcreti - muvekkildenDusulecek;
  const ofiseKalan = gelenToplamOdeme - muvekkileOdenecek;
  const giderSonrasi = ofiseKalan - yargilamaToplam;
  const autoBerile = giderSonrasi / 2;
  const berileVerilecek = calc.berileManuel ? parseNum(calc.berileVerilecek) : autoBerile;

  return {
    asilAlacak,
    oran,
    muvekkildenDusulecek,
    gelenToplamOdeme,
    faiz,
    faizDetay,
    araToplam,
    avukatlikUcreti,
    muvekkileOdenecek,
    ofiseKalan,
    yargilamaToplam,
    giderSonrasi,
    autoBerile,
    berileVerilecek
  };
};

const HesapKarti = ({
  calc,
  sonuc,
  index,
  giderler,
  onChange,
  onRemove,
  onToggleYargilama,
  onResetBerile,
  onMasrafaAktar,
  masrafaAktariliyor,
  linked,
  acik,
  onToggleAcik
}) => {
  const dahilIds = new Set((calc.yargilamaDahilIds || []).map(Number));
  const berileValue = calc.berileManuel
    ? calc.berileVerilecek
    : (Number.isFinite(sonuc.autoBerile) ? (Math.round(sonuc.autoBerile * 100) / 100) : '');

  return (
    <div className={`hd-card ${acik ? 'is-open' : 'is-collapsed'}`}>
      <div
        className="hd-card-head"
        onClick={onToggleAcik}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          // Yalnız başlık satırının kendisi: içindeki ad kutusuna yazılan
          // boşluk ve Enter buraya da ulaşıyor, boşluk yazdırmıyor ve kartı
          // açıp kapatıyordu (2026-10-07: "boşluk koyamıyorum").
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleAcik();
          }
        }}
      >
        <span className="hd-card-chevron" aria-hidden="true">{acik ? '▾' : '▸'}</span>
        <span className="hd-card-index">{index + 1}</span>
        <input
          className="hd-card-title"
          value={calc.baslik}
          placeholder="Ödeme başlığı"
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onChange(calc.id, 'baslik', e.target.value)}
        />
        {!acik && (
          <span className="hd-card-preview">{formatCurrency(sonuc.gelenToplamOdeme)}</span>
        )}
        <button
          type="button"
          className="hd-card-remove"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(calc.id);
          }}
          title="Bu hesaplamayı sil"
          aria-label="Hesaplamayı sil"
        >
          ×
        </button>
      </div>

      {acik && (
      <div className="hd-card-body">

      <div className="hd-top-card">
        <label className="hd-top-label">
          Gelen Ödeme
          {linked && <span className="hd-linked-hint">masraftan</span>}
        </label>
        <div className="hd-input-wrap hd-input-wrap--lg">
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            className="hd-input"
            placeholder="0,00"
            value={calc.gelenToplamOdeme}
            readOnly={linked}
            onChange={(e) => onChange(calc.id, 'gelenToplamOdeme', e.target.value)}
          />
          <span className="hd-suffix">₺</span>
        </div>
      </div>

      <div className="hd-inputs">
        <div className="hd-row">
          <label className="hd-label">Asıl Alacak</label>
          <div className="hd-input-wrap">
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              className="hd-input"
              placeholder="0,00"
              value={calc.asilAlacak}
              onChange={(e) => onChange(calc.id, 'asilAlacak', e.target.value)}
            />
            <span className="hd-suffix">₺</span>
          </div>
        </div>

        <div className="hd-faiz-block">
          <div className="hd-faiz-head">
            <label className="hd-label">Faiz (Yasal Faiz)</label>
            <span className="hd-faiz-amount">{formatCurrency(sonuc.faiz)}</span>
          </div>
          <div className="hd-faiz-dates">
            <div className="hd-date-field">
              <span className="hd-date-label">Başlangıç Tarihi</span>
              <TarihSecici
                value={calc.faizBaslangic}
                onChange={(v) => onChange(calc.id, 'faizBaslangic', v)}
              />
              {calc.faizBaslangic && (
                <span className="hd-date-display">{formatTarihTR(calc.faizBaslangic)}</span>
              )}
            </div>
            <div className="hd-date-field">
              <span className="hd-date-label">Bitiş Tarihi</span>
              <TarihSecici
                value={calc.faizBitis}
                onChange={(v) => onChange(calc.id, 'faizBitis', v)}
              />
              {calc.faizBitis && (
                <span className="hd-date-display">{formatTarihTR(calc.faizBitis)}</span>
              )}
            </div>
          </div>
          {sonuc.faizDetay.segmentler.length > 0 && (
            <div className="hd-faiz-info">
              <span className="hd-faiz-total">
                Toplam {sonuc.faizDetay.toplamGun} gün — asıl alacak üzerinden
              </span>
              <div className="hd-faiz-segments">
                {sonuc.faizDetay.segmentler.map((s, idx) => (
                  <span key={idx} className="hd-faiz-chip">
                    %{s.oran} · {s.gun} gün · {formatCurrency(s.faiz)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="hd-aratoplam">
          <span className="hd-aratoplam-label">Asıl Alacak + Faiz</span>
          <span className="hd-aratoplam-value">{formatCurrency(sonuc.araToplam)}</span>
        </div>

        <div className="hd-row hd-row--fee">
          <label className="hd-label">Avukatlık Sözleşme Oranı</label>
          <div className="hd-fee-controls hd-sozlesme-oran-row">
            <div className="hd-input-wrap hd-input-wrap--sm">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                className="hd-input"
                placeholder="0"
                value={calc.avukatlikOrani}
                onChange={(e) => onChange(calc.id, 'avukatlikOrani', e.target.value)}
              />
              <span className="hd-suffix">%</span>
            </div>
            <label className="hd-checkbox sozlesme-kdv-check">
              <input
                type="checkbox"
                checked={!!calc.kdvDahil}
                onChange={(e) => onChange(calc.id, 'kdvDahil', e.target.checked)}
              />
              <span>KDV dahil</span>
            </label>
            <span className="hd-fee-amount" title="Hesaplanan avukatlık ücreti">
              = {formatCurrency(sonuc.avukatlikUcreti)}
            </span>
          </div>
        </div>

        <div className="hd-row">
          <label className="hd-label">Müvekkilden Düşülecek Masraf</label>
          <div className="hd-input-wrap">
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              className="hd-input"
              placeholder="0,00"
              value={calc.muvekkildenDusulecekMasraf}
              onChange={(e) => onChange(calc.id, 'muvekkildenDusulecekMasraf', e.target.value)}
            />
            <span className="hd-suffix">₺</span>
          </div>
        </div>

        <details className="hd-details">
          <summary>
            <span>Yapılan Yargılama Giderleri</span>
            <span className="hd-details-sum">{formatCurrency(sonuc.yargilamaToplam)}</span>
          </summary>
          <div className="hd-masraf-kalemler">
            {(giderler || []).length === 0 ? (
              <p className="hd-masraf-hint">Gider masrafı yok. Masraflar bölümünden gider ekleyin.</p>
            ) : (
              giderler.map((m) => {
                const id = masrafIdOf(m);
                if (id == null) return null;
                return (
                  <label className="hd-masraf-kalem" key={id}>
                    <input
                      type="checkbox"
                      checked={dahilIds.has(id)}
                      onChange={() => onToggleYargilama(calc.id, id)}
                    />
                    <span>{masrafTuruOf(m) || 'Gider'}</span>
                    <span>{formatCurrency(masrafTutarOf(m))}</span>
                  </label>
                );
              })
            )}
          </div>
          <p className="hd-masraf-hint">
            Ofise kalandan düşülecek giderleri işaretleyin. Müvekkile yapılan ödemeler burada yer almaz.
          </p>
        </details>
      </div>

      <div className="hd-card-results">
        <div className="hd-mini-result hd-mini-result--client">
          <span className="hd-mini-label">Müvekkile Ödenecek</span>
          <span className="hd-mini-value">{formatCurrency(sonuc.muvekkileOdenecek)}</span>
        </div>
        <div
          className={`hd-mini-result hd-mini-result--office ${
            sonuc.ofiseKalan < 0 ? 'is-negative' : ''
          }`}
        >
          <span className="hd-mini-label">Ofise Kalan</span>
          <span className="hd-mini-value">{formatCurrency(sonuc.ofiseKalan)}</span>
        </div>
        <div
          className={`hd-mini-result hd-mini-result--gider ${
            sonuc.giderSonrasi < 0 ? 'is-negative' : ''
          }`}
        >
          <span className="hd-mini-label">Gider sonrası kalan</span>
          <span className="hd-mini-value">{formatCurrency(sonuc.giderSonrasi)}</span>
        </div>
        <div className="hd-mini-result hd-mini-result--berile">
          <span className="hd-mini-label">Berile verilecek</span>
          <div className="hd-berile-row">
            <div className="hd-input-wrap hd-input-wrap--sm">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                className="hd-input"
                value={berileValue}
                onChange={(e) => onChange(calc.id, 'berileVerilecek', e.target.value)}
              />
              <span className="hd-suffix">₺</span>
            </div>
            {calc.berileManuel ? (
              <button type="button" className="hd-berile-reset" onClick={() => onResetBerile(calc.id)}>
                Otomatik (½)
              </button>
            ) : (
              <span className="hd-berile-hint">kalanın yarısı</span>
            )}
          </div>
        </div>
      </div>

        <div className="hd-masrafa-aktar">
          <button
            type="button"
            className="hd-masrafa-btn"
            disabled={!!masrafaAktariliyor}
            onClick={() => onMasrafaAktar(calc, sonuc)}
          >
            {masrafaAktariliyor
              ? 'Ekleniyor…'
              : calc.masrafaAktarildi
                ? 'Masraflara tekrar ekle'
                : 'Hesaplamayı masraflara ekle'}
          </button>
          <span className="hd-masrafa-hint">
            Müvekkile ödenecek ve berile verilecek tutarlar masraf olarak işlenir.
          </span>
        </div>
      </div>
      )}
    </div>
  );
};

const normalizeHesaplar = (parsed) => {
  if (Array.isArray(parsed) && parsed.length > 0) {
    return parsed.map((c) => ({ ...yeniHesap(), ...c, id: c.id || yeniId() }));
  }
  if (parsed && typeof parsed === 'object') {
    const hasData = KNOWN_KEYS.some((k) => parsed[k] !== undefined && parsed[k] !== '');
    if (hasData) {
      return [{ ...yeniHesap('1. Ödeme'), ...parsed }];
    }
  }
  return null;
};

const hasMeaningfulData = (list) =>
  Array.isArray(list) &&
  list.some((c) => {
    if (c?.masrafId != null) return true;
    if (Array.isArray(c?.yargilamaDahilIds) && c.yargilamaDahilIds.length > 0) return true;
    return KNOWN_KEYS.some((k) => {
      const v = c?.[k];
      return v !== undefined && v !== null && v !== '' && v !== false;
    });
  });

const parseStored = (raw) => {
  if (!raw) return { hesaplar: null, ignoredMasrafIds: [] };
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed)) {
      return { hesaplar: parsed.length ? normalizeHesaplar(parsed) : [], ignoredMasrafIds: [] };
    }
    if (parsed && typeof parsed === 'object') {
      if (Array.isArray(parsed.hesaplar)) {
        return {
          hesaplar: parsed.hesaplar.length ? normalizeHesaplar(parsed.hesaplar) : [],
          ignoredMasrafIds: (parsed.ignoredMasrafIds || []).map(Number).filter((n) => Number.isFinite(n))
        };
      }
      const one = normalizeHesaplar(parsed);
      return { hesaplar: one, ignoredMasrafIds: [] };
    }
  } catch {
    /* ignore */
  }
  return { hesaplar: null, ignoredMasrafIds: [] };
};

const readLocalStored = (key) => {
  try {
    return parseStored(localStorage.getItem(key));
  } catch {
    return { hesaplar: null, ignoredMasrafIds: [] };
  }
};

const persistPayload = (hesaplar, ignoredMasrafIds) => ({
  v: 2,
  hesaplar: hesaplar || [],
  ignoredMasrafIds: ignoredMasrafIds || []
});

const hesapSnapshot = (list) =>
  JSON.stringify(
    (list || []).map((c) => ({
      id: c.id,
      masrafId: c.masrafId ?? null,
      baslik: c.baslik,
      gelenToplamOdeme: c.gelenToplamOdeme,
      avukatlikOrani: c.avukatlikOrani,
      kdvDahil: !!c.kdvDahil,
      yargilamaDahilIds: [...(c.yargilamaDahilIds || [])].map(Number).sort((a, b) => a - b)
    }))
  );

const syncHesaplarWithMasraflar = (hesaplar, masraflar, caseFile, knownGiderRef, ignoredMasrafIds) => {
  const ignored = new Set((ignoredMasrafIds || []).map(Number));
  const gelenler = (masraflar || []).filter(isGelenOdemeMasraf);
  const gelenAktarilacak = gelenler.filter((m) => isMasrafBugunVeyaSonra(m) && !ignored.has(masrafIdOf(m)));
  const giderler = (masraflar || []).filter(isYargilamaGiderMasraf);
  const giderIds = giderler.map(masrafIdOf).filter((id) => id != null);
  const giderIdSet = new Set(giderIds);
  const oranStr = sozlesmeOraniStr(caseFile);
  const kdv = sozlesmeKdvDahil(caseFile);
  const droppedIds = [];

  let next = (hesaplar || []).filter((c) => {
    if (c.masrafId == null) return true;
    const id = Number(c.masrafId);
    if (ignored.has(id)) return false;
    const m = gelenler.find((x) => masrafIdOf(x) === id);
    if (m && !isMasrafBugunVeyaSonra(m)) {
      droppedIds.push(id);
      return false;
    }
    return true;
  }).map((c) => {
    const copy = { ...c };
    if (copy.masrafId != null) {
      const m = gelenAktarilacak.find((x) => masrafIdOf(x) === Number(copy.masrafId));
      if (m) {
        if (!copy.baslikManuel) copy.baslik = masrafTuruOf(m) || copy.baslik;
        copy.gelenToplamOdeme = String(masrafTutarOf(m));
      }
    }
    if (!copy.oranManuel && (copy.avukatlikOrani === '' || copy.avukatlikOrani == null) && oranStr) {
      copy.avukatlikOrani = oranStr;
      copy.kdvDahil = kdv;
    }
    if (Array.isArray(copy.yargilamaDahilIds)) {
      copy.yargilamaDahilIds = copy.yargilamaDahilIds.map(Number).filter((id) => giderIdSet.has(id));
    }
    return copy;
  });

  gelenAktarilacak.forEach((m) => {
    const id = masrafIdOf(m);
    if (id == null || next.some((c) => Number(c.masrafId) === id)) return;
    const card = yeniHesap(masrafTuruOf(m) || 'Gelen Ödeme');
    card.masrafId = id;
    card.gelenToplamOdeme = String(masrafTutarOf(m));
    if (oranStr) {
      card.avukatlikOrani = oranStr;
      card.kdvDahil = kdv;
    }
    next.push(card);
  });

  const anyInitialized = next.some((c) => Array.isArray(c.yargilamaDahilIds));
  next = next.map((c, i) => {
    if (Array.isArray(c.yargilamaDahilIds)) return c;
    return { ...c, yargilamaDahilIds: !anyInitialized && i === 0 ? [...giderIds] : [] };
  });

  const known = knownGiderRef.current;
  const isFirstKnown = known.size === 0;
  const newGiderIds = giderIds.filter((id) => !known.has(id));
  knownGiderRef.current = new Set(giderIds);
  if (!isFirstKnown && newGiderIds.length && next[0]) {
    const set = new Set((next[0].yargilamaDahilIds || []).map(Number));
    newGiderIds.forEach((id) => set.add(id));
    next = next.map((c, i) => (i === 0 ? { ...c, yargilamaDahilIds: [...set] } : c));
  }

  return { next, droppedIds };
};

const HesapDetayi = ({
  caseFileId,
  caseFile = null,
  masraflar = [],
  loadingMasraflar = false,
  onUpdated,
  onMasraflarChanged
}) => {
  const storageKey = `hesapDetayi_${caseFileId}`;
  const [hesaplar, setHesaplar] = useState(null);
  const [ignoredMasrafIds, setIgnoredMasrafIds] = useState([]);
  const [kayitDurum, setKayitDurum] = useState(null);
  const [kapali, setKapali] = useState({});
  const [masrafaAktarId, setMasrafaAktarId] = useState(null);
  const hydratedKeyRef = useRef(null);
  const saveTimerRef = useRef(null);
  const skipNextSaveRef = useRef(false);
  const onUpdatedRef = useRef(onUpdated);
  const knownGiderRef = useRef(new Set());
  onUpdatedRef.current = onUpdated;

  const sozlesmeKey = `${sozlesmeOraniStr(caseFile)}|${sozlesmeKdvDahil(caseFile) ? 1 : 0}`;
  const masrafSyncKey = (masraflar || [])
    .map((m) => `${masrafIdOf(m)}:${masrafTuruOf(m)}:${masrafTutarOf(m)}:${masrafTarihKey(m)}:${m?.IsGelir || m?.isGelir ? 1 : 0}`)
    .join('|');
  const hesaplarHazir = hesaplar != null;
  const ignoredKey = (ignoredMasrafIds || []).join(',');

  useEffect(() => {
    if (!caseFileId) return;
    let cancelled = false;
    hydratedKeyRef.current = null;
    knownGiderRef.current = new Set();
    setHesaplar(null);

    const load = async () => {
      let stored = parseStored(caseFile?.HesapDetayiJson ?? caseFile?.hesapDetayiJson);
      try {
        const cf = await caseFileAPI.getById(caseFileId);
        const apiParsed = parseStored(cf?.HesapDetayiJson ?? cf?.hesapDetayiJson);
        if (apiParsed.hesaplar != null) stored = apiParsed;
      } catch {
        /* ağ hatasında prop/local'e düş */
      }

      const fromLocal = readLocalStored(storageKey);
      const yuklenen = stored.hesaplar != null
        ? stored.hesaplar
        : (fromLocal.hesaplar != null ? fromLocal.hesaplar : []);
      const ignored = [
        ...new Set([
          ...(stored.ignoredMasrafIds || []),
          ...(fromLocal.ignoredMasrafIds || [])
        ])
      ];

      if (cancelled) return;

      skipNextSaveRef.current = true;
      hydratedKeyRef.current = storageKey;
      setIgnoredMasrafIds(ignored);
      setHesaplar(yuklenen);
      setKapali({});

      if (!stored.hesaplar && hasMeaningfulData(fromLocal.hesaplar)) {
        try {
          const updated = await caseFileAPI.updateHesapDetayi(
            caseFileId,
            persistPayload(fromLocal.hesaplar, fromLocal.ignoredMasrafIds)
          );
          if (onUpdatedRef.current && updated) onUpdatedRef.current(updated);
        } catch {
          /* sessiz */
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [caseFileId, storageKey]);

  useEffect(() => {
    if (hesaplar == null) return;
    if (hydratedKeyRef.current !== storageKey) return;
    if (loadingMasraflar) return;

    setHesaplar((prev) => {
      if (!prev) return prev;
      const { next, droppedIds } = syncHesaplarWithMasraflar(
        prev,
        masraflar,
        caseFile,
        knownGiderRef,
        ignoredMasrafIds
      );
      if (droppedIds.length) {
        setIgnoredMasrafIds((p) => [...new Set([...(p || []), ...droppedIds])]);
      }
      if (hesapSnapshot(next) === hesapSnapshot(prev)) return prev;
      return next;
    });
  }, [hesaplarHazir, loadingMasraflar, masrafSyncKey, sozlesmeKey, storageKey, masraflar, caseFile, ignoredKey]);

  const persistServer = useCallback(
    async (list, ignored) => {
      if (!caseFileId || list == null) return;
      setKayitDurum('saving');
      try {
        const updated = await caseFileAPI.updateHesapDetayi(
          caseFileId,
          persistPayload(list, ignored)
        );
        setKayitDurum('ok');
        if (onUpdatedRef.current && updated) onUpdatedRef.current(updated);
      } catch {
        setKayitDurum('err');
      }
    },
    [caseFileId]
  );

  useEffect(() => {
    if (hesaplar == null) return;
    if (hydratedKeyRef.current !== storageKey) return;

    const payload = persistPayload(hesaplar, ignoredMasrafIds);
    try {
      localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch {
      /* ignore */
    }

    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      persistServer(hesaplar, ignoredMasrafIds);
    }, 600);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [hesaplar, ignoredMasrafIds, storageKey, persistServer]);

  const handleChange = (id, field, value) => {
    setHesaplar((prev) =>
      (prev || []).map((c) => {
        if (c.id !== id) return c;
        if (field === 'baslik') return { ...c, baslik: value, baslikManuel: true };
        if (field === 'berileVerilecek') return { ...c, berileVerilecek: value, berileManuel: true };
        if (field === 'avukatlikOrani' || field === 'kdvDahil') {
          return { ...c, [field]: value, oranManuel: true };
        }
        return { ...c, [field]: value };
      })
    );
  };

  const handleRemove = (id) => {
    setHesaplar((prev) => {
      const card = (prev || []).find((c) => c.id === id);
      if (card?.masrafId != null) {
        const mid = Number(card.masrafId);
        setIgnoredMasrafIds((p) => [...new Set([...(p || []), mid])]);
      }
      return (prev || []).filter((c) => c.id !== id);
    });
  };

  const handleAdd = () => {
    const oranStr = sozlesmeOraniStr(caseFile);
    const card = yeniHesap(`${(hesaplar || []).length + 1}. Ödeme`);
    if (oranStr) {
      card.avukatlikOrani = oranStr;
      card.kdvDahil = sozlesmeKdvDahil(caseFile);
    }
    card.yargilamaDahilIds = [];
    setHesaplar((prev) => [...(prev || []), card]);
    setKapali((p) => ({ ...p, [card.id]: false }));
  };

  const handleToggleAcik = (id, index) => {
    setKapali((p) => {
      const currentlyOpen = p[id] === true ? false : p[id] === false ? true : index === 0;
      return { ...p, [id]: currentlyOpen };
    });
  };

  const handleMasrafaAktar = async (calc, sonuc) => {
    if (!caseFileId || masrafaAktarId) return;
    const muvekkile = round2(sonuc.muvekkileOdenecek);
    const berile = round2(sonuc.berileVerilecek);
    if (muvekkile <= 0 && berile <= 0) {
      alert('Masrafa aktarılacak tutar yok. Önce asıl alacak ve oranı girin.');
      return;
    }
    const satirlar = [];
    if (muvekkile > 0) satirlar.push(`Müvekkile ödenecek: ${formatCurrency(muvekkile)}`);
    if (berile > 0) satirlar.push(`Berile verilecek: ${formatCurrency(berile)}`);
    if (!await onayla(`Bu hesaplama masraflara eklensin mi?\n\n${satirlar.join('\n')}`)) return;

    setMasrafaAktarId(calc.id);
    const todayISO = getTodayISOString();
    const aciklama = calc.baslik ? `Hesap detayı: ${calc.baslik}` : 'Hesap detayı';
    try {
      if (muvekkile > 0) {
        await masrafAPI.create({
          CaseFileId: parseInt(caseFileId, 10),
          MasrafTuru: 'MÜVEKKİLE YAPILAN ÖDEME',
          Tutar: muvekkile,
          Tarih: todayISO,
          MasrafIstenmeTarihi: todayISO,
          SonTarih: todayISO,
          Aciklama: aciklama,
          IsGelir: false,
          IsBerileVerilen: false
        });
      }
      if (berile > 0) {
        await masrafAPI.create({
          CaseFileId: parseInt(caseFileId, 10),
          MasrafTuru: 'BERİLE VERİLEN',
          Tutar: berile,
          Tarih: todayISO,
          MasrafIstenmeTarihi: todayISO,
          SonTarih: todayISO,
          Aciklama: aciklama,
          IsGelir: false,
          IsBerileVerilen: true
        });
      }
      setHesaplar((prev) =>
        (prev || []).map((c) => (c.id === calc.id ? { ...c, masrafaAktarildi: true } : c))
      );
      if (onMasraflarChanged) await onMasraflarChanged();
    } catch (error) {
      alert('Masrafa eklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    } finally {
      setMasrafaAktarId(null);
    }
  };

  const handleToggleYargilama = (calcId, masrafId) => {
    setHesaplar((prev) =>
      (prev || []).map((c) => {
        if (c.id !== calcId) return c;
        const ids = new Set((c.yargilamaDahilIds || []).map(Number));
        if (ids.has(masrafId)) ids.delete(masrafId);
        else ids.add(masrafId);
        return { ...c, yargilamaDahilIds: [...ids] };
      })
    );
  };

  const handleResetBerile = (id) => {
    setHesaplar((prev) =>
      (prev || []).map((c) => (c.id === id ? { ...c, berileManuel: false, berileVerilecek: '' } : c))
    );
  };

  const giderler = useMemo(
    () => (masraflar || []).filter(isYargilamaGiderMasraf),
    [masraflar]
  );
  const gelenler = useMemo(
    () => (masraflar || []).filter(isGelenOdemeMasraf),
    [masraflar]
  );

  const liste = hesaplar || [];
  const sonuclar = useMemo(
    () => liste.map((c) => hesapla(c, yargilamaToplamOf(c, giderler))),
    [liste, giderler]
  );

  const toplamlar = useMemo(() => {
    return sonuclar.reduce(
      (acc, s) => {
        acc.gelenToplamOdeme += s.gelenToplamOdeme;
        acc.muvekkileOdenecek += s.muvekkileOdenecek;
        acc.ofiseKalan += s.ofiseKalan;
        acc.berileVerilecek += s.berileVerilecek;
        return acc;
      },
      { gelenToplamOdeme: 0, muvekkileOdenecek: 0, ofiseKalan: 0, berileVerilecek: 0 }
    );
  }, [sonuclar]);

  if (hesaplar == null) {
    return (
      <div className="hesap-detayi">
        <p style={{ color: '#64748b', margin: '12px 0' }}>Hesaplamalar yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="hesap-detayi">
      <div className="hd-summary-grid">
        <div className="hd-summary hd-summary--income">
          <span className="hd-summary-label">Dosyaya Gelen Toplam Ödeme</span>
          <span className="hd-summary-value">{formatCurrency(toplamlar.gelenToplamOdeme)}</span>
        </div>
        <div className="hd-summary hd-summary--client">
          <span className="hd-summary-label">Müvekkile Toplam Ödenen</span>
          <span className="hd-summary-value">{formatCurrency(toplamlar.muvekkileOdenecek)}</span>
        </div>
        <div className={`hd-summary hd-summary--office ${toplamlar.ofiseKalan < 0 ? 'is-negative' : ''}`}>
          <span className="hd-summary-label">Toplam Ofise Kalan</span>
          <span className="hd-summary-value">{formatCurrency(toplamlar.ofiseKalan)}</span>
        </div>
        <div className="hd-summary hd-summary--berile">
          <span className="hd-summary-label">Berile Verilecek</span>
          <span className="hd-summary-value">{formatCurrency(toplamlar.berileVerilecek)}</span>
        </div>
      </div>

      <div className="hd-list-header">
        <h3 className="hd-list-title">
          Hesaplamalar <span className="hd-count-badge">{liste.length}</span>
          {kayitDurum === 'saving' && <span className="hd-save-hint">Kaydediliyor…</span>}
          {kayitDurum === 'ok' && <span className="hd-save-hint hd-save-hint--ok">Kaydedildi</span>}
          {kayitDurum === 'err' && <span className="hd-save-hint hd-save-hint--err">Kayıt hatası</span>}
        </h3>
        <button type="button" className="hd-add-btn" onClick={handleAdd}>
          + Yeni Hesaplama
        </button>
      </div>

      <div className="hd-cards">
        {liste.length === 0 ? (
          <p className="hd-empty">Henüz hesaplama yok. Gelen ödeme ekleyin veya yeni hesaplama oluşturun.</p>
        ) : (
          liste.map((calc, index) => {
            const acik =
              liste.length <= 1
                ? true
                : kapali[calc.id] === true
                  ? false
                  : kapali[calc.id] === false
                    ? true
                    : index === 0;
            return (
          <HesapKarti
            key={calc.id}
            calc={calc}
            sonuc={sonuclar[index]}
            index={index}
            giderler={giderler}
            onChange={handleChange}
            onRemove={handleRemove}
            onToggleYargilama={handleToggleYargilama}
            onResetBerile={handleResetBerile}
            onMasrafaAktar={handleMasrafaAktar}
            masrafaAktariliyor={masrafaAktarId === calc.id}
            linked={gelenler.some((m) => masrafIdOf(m) === Number(calc.masrafId))}
            acik={acik}
            onToggleAcik={() => handleToggleAcik(calc.id, index)}
          />
            );
          })
        )}
      </div>
    </div>
  );
};

export default HesapDetayi;
